import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FaCamera, FaCheckCircle, FaExclamationTriangle, FaUserCircle } from "react-icons/fa";
import { FiPaperclip, FiPlus } from "react-icons/fi";
import { LuPencil, LuTrash2 } from "react-icons/lu";
import ReactQuill from "react-quill-new";
import { MdKeyboardArrowDown } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import AttachmentViewerModal from "../../components/modals/AttachmentViewerModal";
import CnicInput from "../../components/CnicInput";
import CustomTabs from "../../components/CustomTabs";
import FileUpload from "../../components/FileUpload";
import Input from "../../components/input";
import LogsComments from "../../components/LogsComments";
import MultiLineText from "../../components/MultiLineText";
import NewTableComponent from "../../components/NewTableComponent";
import PhoneNumberInput from "../../components/PhoneNumberInput";
import Select from "../../components/Select";
import { apiService } from "../../services/apiService";
import { uploadFiles } from "../../store/Slices/FileSlice";
import {
  commentOnDriver,
  fetchDriverAttendance,
  fetchDriverDetail,
  fetchDriverHistory,
  fetchDriverExpensesPaginated,
  fetchDriverLeaves,
  fetchDriverPayroll,
  generateDriverPassword,
  updateDriver,
} from "../../store/Slices/DriverSlice";
import { fetchUsersDropdown } from "../../store/Slices/dropdownSlice";
import "../../styles/Access.css";
import "../../styles/AssetsRegistration.css";
import "../../styles/Input.css";
import "../../styles/Issue.css";
import "../../styles/Trip.css";
import "../../styles/DriverSafety.css";
import {
  AppMainModules,
  DriverSafetyPermission,
  OperationsSubModules,
} from "../../utils/app-constant";
import { formats, hasPermission, modules } from "../../utils/helper";
import { downloadIcon, refreshIcon } from "../../utils/svgs";
import { errorNotify, successNotify } from "../../utils/toast";
import { driverFormSchema } from "./addDriver.schema";

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const AUTH_METHOD_OPTIONS = [
  { label: "QR Code + OTP (Primary)", value: "qr_otp_primary" },
  { label: "OTP Only", value: "otp_only" },
];

const SALARY_TERMS_OPTIONS = [
  { label: "Monthly", value: "monthly" },
  { label: "Weekly", value: "weekly" },
];

const ALLOWANCE_TYPE_OPTIONS = [
  { label: "Food Allowance", value: "food" },
  { label: "TA/DA", value: "tada" },
  { label: "Trip Allowance", value: "trip" },
  { label: "Mobile Allowance", value: "mobile" },
];

const ALLOWANCE_FREQUENCY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Monthly", value: "monthly" },
  { label: "Occasionally", value: "occasionally" },
];

const ARREARS_DIRECTION_OPTIONS = [
  { label: "Give to Driver (Add to Pay)", value: "arrears_give" },
  { label: "Take from Driver (Deduct from Pay)", value: "arrears_take" },
];

const BONUS_DEDUCTION_FREQUENCY_OPTIONS = [
  { label: "Monthly", value: "monthly" },
  { label: "One-time", value: "one_time" },
];

const ALLOWANCE_TYPE_LABELS = {
  food: "Food Allowance",
  tada: "TA/DA",
  trip: "Trip Allowance",
  mobile: "Mobile Allowance",
  arrears_give: "Arrears (Give to Driver)",
  arrears_take: "Arrears (Take from Driver)",
};

const ALLOWANCE_SECTION_TYPES = ["food", "tada", "trip", "mobile"];
const ARREARS_SECTION_TYPES = ["arrears_give", "arrears_take"];

const EMPTY_ALLOWANCE_FORM = {
  editingId: null,
  allowanceType: "",
  amount: "",
  frequency: "",
};

const EMPTY_ARREARS_FORM = {
  editingId: null,
  direction: "",
  amount: "",
  frequency: "monthly",
};

const MONTH_LABELS = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const PROFILE_PICTURE_MAX_SIZE_BYTES = 2 * 1024 * 1024;
const DEFAULT_AUTH_METHOD = AUTH_METHOD_OPTIONS[0].value;
const DEFAULT_SALARY_TERM = SALARY_TERMS_OPTIONS[0].value;
const SHOW_DRIVER_TRAININGS_TAB = false;

const DRIVER_STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  on_leave: "On Leave",
  onleave: "On Leave",
};

const toDigits = (value) => String(value || "").replace(/\D/g, "");
const normalizeArray = (value) => (Array.isArray(value) ? value : []);
const isDirectAssetUrl = (value) => /^(https?:)?\/\//i.test(String(value || "").trim());
const isSpecialAssetUrl = (value) => /^(blob:|data:)/i.test(String(value || "").trim());
const isHtmlContent = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));

const normalizeRichText = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  return isHtmlContent(rawValue) ? rawValue : `<p>${rawValue}</p>`;
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\u200B/g, "")
    .replace(/\s+/g, " ")
    .trim();

const buildTouchedFromErrors = (errors) => {
  if (!errors || typeof errors !== "object") return true;
  if (Array.isArray(errors)) return errors.map(buildTouchedFromErrors);

  return Object.keys(errors).reduce((touchedFields, key) => {
    touchedFields[key] = buildTouchedFromErrors(errors[key]);
    return touchedFields;
  }, {});
};

const getNotifyMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  if (Array.isArray(error)) return error.find(Boolean) || fallback;
  if (error && typeof error === "object") {
    return error.message || error.error || error.msg || fallback;
  }
  return fallback;
};

const getFileNameFromPath = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  const segments = rawValue.split("/").filter(Boolean);
  return segments[segments.length - 1] || rawValue;
};

const getFileExtensionFromUrl = (url, fallback = "png") => {
  const cleanUrl = String(url || "").split("?")[0];
  const extension = cleanUrl.split(".").pop();
  return extension && extension.length <= 5 ? extension : fallback;
};

const sanitizeDownloadName = (value) =>
  String(value || "driver")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "driver";

const formatIdCardPhone = (value) => {
  const rawValue = String(value || "").trim();
  const digits = toDigits(rawValue);
  if (!digits) return "";
  if (rawValue.startsWith("+")) return `+${digits}`;
  if (digits.startsWith("92")) return `+${digits}`;
  if (digits.startsWith("0")) return `+92${digits.slice(1)}`;
  return `+${digits}`;
};

const downloadRemoteImage = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Unable to download image");

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
    return;
  } catch {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
};

const normalizeDriverAttachmentEntry = (attachment) => {
  const rawPath =
    attachment?.file_path ||
    attachment?.fileName ||
    attachment?.file_name ||
    attachment?.path ||
    attachment?.url ||
    attachment?.image ||
    "";

  const normalizedPath = String(rawPath || "").trim();
  if (!normalizedPath) return null;

  return {
    ...(attachment && typeof attachment === "object" ? attachment : {}),
    file_path: normalizedPath,
    fileName: normalizedPath,
    originalName: String(
      attachment?.originalName ||
      attachment?.original_name ||
      getFileNameFromPath(normalizedPath)
    ).trim(),
  };
};

const normalizeDriverAttachments = (driverRecord = {}) => {
  if (Array.isArray(driverRecord?.files)) {
    return driverRecord.files.map(normalizeDriverAttachmentEntry).filter(Boolean);
  }
  if (Array.isArray(driverRecord?.attachments)) {
    return driverRecord.attachments.map(normalizeDriverAttachmentEntry).filter(Boolean);
  }
  if (Array.isArray(driverRecord?.driver_files)) {
    return driverRecord.driver_files.map(normalizeDriverAttachmentEntry).filter(Boolean);
  }

  const singleAttachment = driverRecord?.driver_files;
  const normalizedSingleAttachment = normalizeDriverAttachmentEntry(singleAttachment);
  if (normalizedSingleAttachment) {
    return [normalizedSingleAttachment];
  }

  return [];
};

const normalizeStatus = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (DRIVER_STATUS_LABELS[normalizedValue]) {
    return DRIVER_STATUS_LABELS[normalizedValue];
  }

  if (!normalizedValue) return "Inactive";

  return normalizedValue
    .split("_")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
};

const toInputDate = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    return rawValue;
  }

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue.slice(0, 10);
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const formatLastActivity = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) return rawValue;

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTimeOnly = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) return rawValue;

  return parsedDate.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getDriverIdFromEmployeeNumber = (employeeNumber) => {
  const digits = toDigits(employeeNumber);
  return digits ? `DRV-${digits.slice(-6).padStart(6, "0")}` : "";
};

const resolveDriverImageSrc = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (isDirectAssetUrl(rawValue) || isSpecialAssetUrl(rawValue)) return rawValue;
  return `${process.env.REACT_APP_API_FILES_URL}${rawValue}`;
};

const getDriverVehicleLabel = (vehicle) =>
  String(
    vehicle?.registration_number ||
    vehicle?.registration_no ||
    vehicle?.vehicle_registration_number ||
    vehicle?.vehicle_id ||
    vehicle?.name ||
    vehicle?.title ||
    ""
  ).trim();

const normalizeDriverAssignedVehicle = (vehicle, role) => {
  if (!vehicle || typeof vehicle !== "object") return null;

  const label = getDriverVehicleLabel(vehicle);
  if (!label) return null;

  return {
    ...vehicle,
    role,
    label,
    odm: String(vehicle?.odm ?? vehicle?.odo_meter ?? "").trim(),
    imeino: String(vehicle?.imeino || "").trim(),
    currentFuel: String(vehicle?.current_fuel ?? vehicle?.currentFuel ?? "").trim(),
  };
};

const normalizeDriverRecord = (driverRecord = {}) => {
  const user = driverRecord?.user || {};
  const baseSalary = Number(driverRecord?.basic_salary ?? driverRecord?.baseSalary ?? 0);
  const deductions = Number(driverRecord?.deductions ?? driverRecord?.costSummary?.deductions ?? 0);
  const assignedVehicles = [
    normalizeDriverAssignedVehicle(driverRecord?.vehicle_primary_driver, "Primary Driver"),
    normalizeDriverAssignedVehicle(driverRecord?.vehicle_secondary_driver, "Secondary Driver"),
    normalizeDriverAssignedVehicle(driverRecord?.vehicle_helper, "Helper"),
  ].filter(Boolean);

  return {
    id: driverRecord?.id ?? "",
    userId: driverRecord?.user_id ?? driverRecord?.userId ?? "",
    driverId: String(driverRecord?.driver_info_id || driverRecord?.driverId || "").trim(),
    driverName: String(
      user?.username || driverRecord?.user_name || driverRecord?.driverName || ""
    ).trim(),
    employeeNumber: String(
      driverRecord?.employee_id || driverRecord?.employeeNumber || ""
    ).trim(),
    dateOfBirth: toInputDate(driverRecord?.date_of_birth || driverRecord?.dateOfBirth),
    cnicNumber: String(driverRecord?.cnic || driverRecord?.cnicNumber || "").trim(),
    cnicIssueDate: toInputDate(driverRecord?.cnic_issue_at || driverRecord?.cnicIssueDate),
    cnicExpiryDate: toInputDate(
      driverRecord?.cnic_expire_at || driverRecord?.cnicExpiryDate
    ),
    licenseNumber: String(
      driverRecord?.license_number || driverRecord?.licenseNumber || ""
    ).trim(),
    licenseIssueDate: toInputDate(
      driverRecord?.license_issue_at || driverRecord?.licenseIssueDate
    ),
    licenseExpiryDate: toInputDate(
      driverRecord?.license_expire_at || driverRecord?.licenseExpiryDate
    ),
    baseSalary: String(driverRecord?.basic_salary ?? driverRecord?.baseSalary ?? ""),
    salaryTerms: String(
      driverRecord?.salary_term || driverRecord?.salaryTerms || DEFAULT_SALARY_TERM
    ).trim(),
    casualLeaveQuota: (() => { const v = driverRecord?.casual_leave ?? driverRecord?.casualLeaveQuota; return v != null ? String(parseInt(v)) : ""; })(),
    annualLeaveQuota: (() => { const v = driverRecord?.annual_leave ?? driverRecord?.annualLeaveQuota; return v != null ? String(parseInt(v)) : ""; })(),
    sickLeaveQuota: (() => { const v = driverRecord?.sick_leave ?? driverRecord?.sickLeaveQuota; return v != null ? String(parseInt(v)) : ""; })(),
    paternityLeaveQuota: (() => { const v = driverRecord?.paternity_leave ?? driverRecord?.paternityLeaveQuota; return v != null ? String(parseInt(v)) : ""; })(),
    safetyScore: Number(driverRecord?.safety_score ?? driverRecord?.safetyScore ?? 0),
    notes: normalizeRichText(driverRecord?.notes),
    authMethod: String(
      driverRecord?.auth_method || driverRecord?.authMethod || DEFAULT_AUTH_METHOD
    ).trim(),
    appAuthCode: String(
      driverRecord?.driver_auth_code?.password ||
      driverRecord?.auth_code ||
      driverRecord?.appAuthCode ||
      ""
    ).trim(),
    status: normalizeStatus(driverRecord?.status),
    image: String(user?.image || driverRecord?.image || "").trim(),
    contactNumber: String(
      user?.mobile || driverRecord?.contact || driverRecord?.contactNumber || ""
    ).trim(),
    lastActivity: formatLastActivity(
      driverRecord?.updated_at ||
      driverRecord?.updatedAt ||
      driverRecord?.created_at ||
      driverRecord?.createdAt
    ),
    assignedTrips: Number(driverRecord?.assigned_trips ?? driverRecord?.assignedTrips ?? 0),
    assignedVehicles,
    supervisorId: driverRecord?.supervisor_id ?? driverRecord?.supervisorId ?? driverRecord?.supervisor?.id ?? "",
    supervisor: driverRecord?.supervisor || null,
    attachments: normalizeDriverAttachments(driverRecord),
    historyItems: normalizeArray(driverRecord?.historyItems),
    costSummary: {
      tripExpenses: Number(
        driverRecord?.trip_expenses ?? driverRecord?.costSummary?.tripExpenses ?? 0
      ),
      refuelling: Number(
        driverRecord?.refuelling ?? driverRecord?.costSummary?.refuelling ?? 0
      ),
      deductions,
      totalNetSalary: Number(
        driverRecord?.total_net_salary ??
        driverRecord?.costSummary?.totalNetSalary ??
        Math.max(baseSalary - deductions, 0)
      ),
    },
    expenseLogs: normalizeArray(driverRecord?.expense_logs || driverRecord?.expenseLogs),
    attendanceLogs: normalizeArray(
      driverRecord?.attendance_logs || driverRecord?.attendanceLogs
    ),
    leaveLogs: normalizeArray(driverRecord?.leave_logs || driverRecord?.leaveLogs),
    payrollLogs: normalizeArray(driverRecord?.payroll_logs || driverRecord?.payrollLogs),
    trainingLogs: normalizeArray(driverRecord?.training_logs || driverRecord?.trainingLogs),
    trainingScore: Number(
      driverRecord?.training_score ??
      driverRecord?.trainingScore ??
      driverRecord?.safety_score ??
      0
    ),
  };
};

const buildDriverFormValues = (
  driverRecord,
  fallbackEmployeeNumber = "",
  fallbackAuthCode = ""
) => ({
  driverName: driverRecord?.driverName || "",
  employeeNumber: driverRecord?.employeeNumber || fallbackEmployeeNumber,
  cnicNumber: driverRecord?.cnicNumber || "",
  contact: toDigits(driverRecord?.contactNumber).slice(0, 12),
  dateOfBirth: driverRecord?.dateOfBirth || "",
  cnicIssueDate: driverRecord?.cnicIssueDate || "",
  cnicExpiryDate: driverRecord?.cnicExpiryDate || "",
  licenseNumber: driverRecord?.licenseNumber || "",
  licenseIssueDate: driverRecord?.licenseIssueDate || "",
  licenseExpiryDate: driverRecord?.licenseExpiryDate || "",
  appAuthCode: driverRecord?.appAuthCode || fallbackAuthCode,
  authMethod: driverRecord?.authMethod || DEFAULT_AUTH_METHOD,
  baseSalary: String(driverRecord?.baseSalary || ""),
  salaryTerms: driverRecord?.salaryTerms || DEFAULT_SALARY_TERM,
  casualLeaveQuota: String(driverRecord?.casualLeaveQuota || ""),
  annualLeaveQuota: String(driverRecord?.annualLeaveQuota || ""),
  sickLeaveQuota: String(driverRecord?.sickLeaveQuota || ""),
  paternityLeaveQuota: String(driverRecord?.paternityLeaveQuota || ""),
  supervisorId: driverRecord?.supervisorId ?? driverRecord?.supervisor_id ?? driverRecord?.supervisor?.id ?? "",
  attachments: normalizeArray(driverRecord?.attachments),
  notes: driverRecord?.notes || "",
});

const buildDriverRecordFromValues = ({
  values,
  currentDriver = null,
  profileImage = "",
  historyItems = null,
}) => {
  const employeeNumber = values?.employeeNumber || currentDriver?.employeeNumber || "";
  const existingId = currentDriver?.id;
  const baseSalary = Number(values?.baseSalary || 0);
  const deductions = Number(currentDriver?.costSummary?.deductions || 0);

  return {
    id: existingId || Date.now(),
    userId: currentDriver?.userId || "",
    driverId: currentDriver?.driverId || getDriverIdFromEmployeeNumber(employeeNumber),
    driverName: String(values?.driverName || "").trim(),
    employeeNumber,
    cnicNumber: String(values?.cnicNumber || "").trim(),
    contactNumber: `+${toDigits(values?.contact).slice(0, 12)}`,
    dateOfBirth: values?.dateOfBirth || "",
    cnicIssueDate: values?.cnicIssueDate || "",
    cnicExpiryDate: values?.cnicExpiryDate || "",
    licenseNumber: String(values?.licenseNumber || "").trim(),
    licenseIssueDate: values?.licenseIssueDate || "",
    licenseExpiryDate: values?.licenseExpiryDate || "",
    appAuthCode: String(values?.appAuthCode || "").trim(),
    authMethod: values?.authMethod || DEFAULT_AUTH_METHOD,
    baseSalary: String(values?.baseSalary || ""),
    salaryTerms: values?.salaryTerms || DEFAULT_SALARY_TERM,
    casualLeaveQuota: String(values?.casualLeaveQuota || ""),
    annualLeaveQuota: String(values?.annualLeaveQuota || ""),
    sickLeaveQuota: String(values?.sickLeaveQuota || ""),
    paternityLeaveQuota: String(values?.paternityLeaveQuota || ""),
    supervisorId: values?.supervisorId ?? currentDriver?.supervisorId ?? "",
    status: currentDriver?.status || "Active",
    safetyScore: Number(currentDriver?.safetyScore || 100),
    lastActivity: formatLastActivity(new Date().toISOString()),
    assignedTrips: Number(currentDriver?.assignedTrips ?? 0),
    notes: values?.notes || "",
    image: profileImage || currentDriver?.image || "",
    attachments: normalizeArray(values?.attachments),
    historyItems: normalizeArray(historyItems || currentDriver?.historyItems),
    costSummary: {
      tripExpenses: Number(currentDriver?.costSummary?.tripExpenses || 0),
      refuelling: Number(currentDriver?.costSummary?.refuelling || 0),
      deductions,
      totalNetSalary: Math.max(baseSalary - deductions, 0),
    },
    expenseLogs: normalizeArray(currentDriver?.expenseLogs),
    attendanceLogs: normalizeArray(currentDriver?.attendanceLogs),
    leaveLogs: normalizeArray(currentDriver?.leaveLogs),
    payrollLogs: normalizeArray(currentDriver?.payrollLogs),
    trainingLogs: normalizeArray(currentDriver?.trainingLogs),
    trainingScore: Number(currentDriver?.trainingScore ?? currentDriver?.safetyScore ?? 0),
  };
};

const resolveUploadedDriverImagePath = (uploadResponse) => {
  const queue = Array.isArray(uploadResponse) ? [...uploadResponse] : [uploadResponse];
  const visited = new Set();

  while (queue.length) {
    const current = queue.shift();

    if (current === null || current === undefined || current === "") {
      continue;
    }

    if (typeof current === "string") {
      const normalizedValue = current.trim();
      if (normalizedValue && !normalizedValue.startsWith("blob:")) {
        return normalizedValue;
      }
      continue;
    }

    if (typeof current !== "object" || visited.has(current)) {
      continue;
    }
    visited.add(current);

    const directPath =
      current?.file_path ||
      current?.fileName ||
      current?.file_name ||
      current?.file_url ||
      current?.full_url ||
      current?.media_url ||
      current?.path ||
      current?.url ||
      current?.src ||
      current?.document ||
      current?.file ||
      current?.image ||
      (typeof current?.data === "string" ? current.data : "");

    if (String(directPath || "").trim()) {
      return String(directPath).trim();
    }

    [
      current?.data,
      current?.files,
      current?.results,
      current?.items,
      current?.rows,
      current?.media,
      current?.attachments,
      current?.uploadedFiles,
      current?.uploaded_files,
      ...Object.values(current),
    ].forEach((item) => {
      if (item && typeof item === "object") {
        if (Array.isArray(item)) {
          queue.push(...item);
        } else {
          queue.push(item);
        }
      } else if (typeof item === "string" && item.trim()) {
        queue.push(item.trim());
      }
    });
  }

  return "";
};

const buildDriverUpdatePayload = (driverRecord, updatedKeys = []) => {
  const resolvedImage = String(driverRecord?.image || "");
  const uploadedFiles = normalizeArray(driverRecord?.attachments)
    .map((file) => resolveUploadedDriverImagePath(file))
    .filter(Boolean);

  return {
    driver_info_id: Number(driverRecord?.id || 0),
    user_name: String(driverRecord?.driverName || "").trim(),
    employee_id: String(driverRecord?.employeeNumber || "").trim(),
    contact: String(driverRecord?.contactNumber || "").trim(),
    date_of_birth: driverRecord?.dateOfBirth || "",
    cnic: String(driverRecord?.cnicNumber || "").trim(),
    cnic_issue_at: driverRecord?.cnicIssueDate || "",
    cnic_expire_at: driverRecord?.cnicExpiryDate || "",
    license_number: String(driverRecord?.licenseNumber || "").trim(),
    license_issue_at: driverRecord?.licenseIssueDate || "",
    license_expire_at: driverRecord?.licenseExpiryDate || "",
    basic_salary: Number(driverRecord?.baseSalary || 0),
    salary_term: String(driverRecord?.salaryTerms || DEFAULT_SALARY_TERM).trim(),
    casual_leave: Number(driverRecord?.casualLeaveQuota || 0),
    paternity_leave: Number(driverRecord?.paternityLeaveQuota || 0),
    annual_leave: Number(driverRecord?.annualLeaveQuota || 0),
    sick_leave: Number(driverRecord?.sickLeaveQuota || 0),
    safety_score: Number(driverRecord?.safetyScore || 0),
    notes: stripHtml(driverRecord?.notes || ""),
    auth_method: String(driverRecord?.authMethod || DEFAULT_AUTH_METHOD).trim(),
    auth_code: String(driverRecord?.appAuthCode || "").trim(),
    supervisor_id: driverRecord?.supervisorId ? Number(driverRecord.supervisorId) : null,
    image:
      resolvedImage && !resolvedImage.startsWith("blob:")
        ? resolvedImage
        : "",
    files: uploadedFiles,
    updatedKeys,
  };
};

const DRIVER_UPDATE_TRACKED_KEYS = [
  "user_name",
  "employee_id",
  "contact",
  "date_of_birth",
  "cnic",
  "cnic_issue_at",
  "cnic_expire_at",
  "license_number",
  "license_issue_at",
  "license_expire_at",
  "basic_salary",
  "salary_term",
  "casual_leave",
  "paternity_leave",
  "annual_leave",
  "sick_leave",
  "safety_score",
  "notes",
  "auth_method",
  "auth_code",
  "supervisor_id",
  "image",
  "files",
];

const areDriverPayloadValuesEqual = (left, right) => {
  if (Array.isArray(left) || Array.isArray(right)) {
    return JSON.stringify(Array.isArray(left) ? left : []) ===
      JSON.stringify(Array.isArray(right) ? right : []);
  }

  return left === right;
};

const formatDriverAmount = (value) => {
  const numericValue = Number(value || 0);
  if (!Number.isFinite(numericValue)) return "0";
  return numericValue.toLocaleString();
};

const normalizeExpenseLogItem = (expenseLog, index) => {
  const expenseId =
    expenseLog?.expenseId ||
    expenseLog?.expense_id ||
    (expenseLog?.id ? `EXP-${expenseLog.id}` : "");

  const category =
    expenseLog?.expense_category?.title ||
    expenseLog?.trip_expense_category ||
    expenseLog?.category ||
    "";

  const tripId =
    expenseLog?.tripId ||
    (expenseLog?.trip_id ? `TR-${expenseLog.trip_id}` : "") ||
    "";

  return {
    id: expenseLog?.id || `driver-expense-${index + 1}`,
    expenseId,
    category,
    tripId,
    amount: Number(expenseLog?.amount || 0),
    createdDate: formatLastActivity(expenseLog?.createdDate || expenseLog?.created_at),
    files: Array.isArray(expenseLog?.files) ? expenseLog.files : [],
  };
};

const getDriverExpenseLogs = (driverRecord) =>
  normalizeArray(driverRecord?.expenseLogs).map((expenseLog, index) =>
    normalizeExpenseLogItem(expenseLog, index)
  );

const getDriverAttendanceLogs = (driverRecord) =>
  normalizeArray(driverRecord?.attendanceLogs).map((attendanceLog, index) => {
    const checkInLatitude = attendanceLog?.check_in_lat ?? attendanceLog?.checkInLat;
    const checkInLongitude = attendanceLog?.check_in_lng ?? attendanceLog?.checkInLng;
    const geoCoordinates =
      attendanceLog?.geoCoordinates ||
      attendanceLog?.geo_coordinates ||
      [checkInLatitude, checkInLongitude]
        .filter((coordinate) => coordinate !== null && coordinate !== undefined && coordinate !== "")
        .join(", ");
    const category =
      attendanceLog?.category ||
      attendanceLog?.attendance_status ||
      attendanceLog?.attendanceStatus ||
      "";

    return {
      id: attendanceLog?.id || `driver-attendance-${index + 1}`,
      serialNo: Number(attendanceLog?.serialNo || attendanceLog?.serial_no || index + 1),
      attendanceDate: formatLastActivity(
        attendanceLog?.attendanceDate ||
        attendanceLog?.attendance_date ||
        attendanceLog?.check_in_at ||
        attendanceLog?.checkInAt
      ),
      location:
        attendanceLog?.location ||
        attendanceLog?.check_in_location ||
        attendanceLog?.checkInLocation ||
        "",
      geoCoordinates,
      timeIn: formatTimeOnly(
        attendanceLog?.timeIn ||
        attendanceLog?.time_in ||
        attendanceLog?.check_in_at ||
        attendanceLog?.checkInAt
      ),
      category: normalizeStatus(category),
    };
  });

const getDriverLeaveLogs = (driverRecord) =>
  normalizeArray(driverRecord?.leaveLogs).map((leaveLog, index) => {
    const totalDays = Number(leaveLog?.total_days ?? leaveLog?.totalDays ?? 0);
    const fromDate = formatLastActivity(leaveLog?.from_date || leaveLog?.fromDate);
    const toDate = formatLastActivity(leaveLog?.to_date || leaveLog?.toDate);

    return {
      id: leaveLog?.id || `driver-leave-${index + 1}`,
      serialNo: Number(leaveLog?.serialNo || leaveLog?.serial_no || index + 1),
      leaveType: normalizeStatus(leaveLog?.leaveType || leaveLog?.leave_type || ""),
      duration:
        leaveLog?.duration ||
        (totalDays ? `${totalDays} ${totalDays === 1 ? "Day" : "Days"}` : ""),
      fromTill:
        leaveLog?.fromTill ||
        leaveLog?.from_till ||
        [fromDate, toDate].filter(Boolean).join(" - "),
      reason: leaveLog?.reason || "",
      category: normalizeStatus(leaveLog?.category || leaveLog?.status || ""),
    };
  });

const getDriverPayrollLogs = (driverRecord) =>
  normalizeArray(driverRecord?.payrollLogs).map((payrollLog, index) => ({
    id: payrollLog?.id || `driver-payroll-${index + 1}`,
    paymentId: payrollLog?.paymentId || payrollLog?.payment_id || "",
    month:
      payrollLog?.month ||
      [
        MONTH_LABELS[Number(payrollLog?.salary_month)] || payrollLog?.salary_month || "",
        payrollLog?.salary_year || "",
      ]
        .filter(Boolean)
        .join(" "),
    baseSalary: Number(payrollLog?.baseSalary || payrollLog?.base_salary || 0),
    allowance: Number(
      payrollLog?.allowance ?? payrollLog?.total_allowances ?? payrollLog?.trip_incentive ?? 0
    ),
    deduction: Number(payrollLog?.deduction ?? payrollLog?.total_deductions ?? 0),
    status: payrollLog?.status || "",
  }));

const getDriverTrainingLogs = (driverRecord) =>
  normalizeArray(driverRecord?.trainingLogs).map((trainingLog, index) => ({
    id: trainingLog?.id || `driver-training-${index + 1}`,
    videoTitle: trainingLog?.videoTitle || trainingLog?.video_title || "",
    duration: trainingLog?.duration || "",
    watchTime: trainingLog?.watchTime || trainingLog?.watch_time || "--",
    completion: trainingLog?.completion || "Pending",
    progressPercent: Number(
      trainingLog?.progressPercent || trainingLog?.progress_percent || 0
    ),
    lastActivity: formatLastActivity(
      trainingLog?.lastActivity || trainingLog?.last_activity || trainingLog?.updated_at
    ),
  }));

const getDriverTrainingSummary = (driverRecord, trainingLogs = null) => {
  const resolvedTrainingLogs = Array.isArray(trainingLogs)
    ? trainingLogs
    : getDriverTrainingLogs(driverRecord);

  const completedCount = resolvedTrainingLogs.filter(
    (training) => String(training?.completion || "").trim().toLowerCase() === "completed"
  ).length;
  const pendingCount = resolvedTrainingLogs.filter(
    (training) => String(training?.completion || "").trim().toLowerCase() === "pending"
  ).length;

  return {
    score: Number(driverRecord?.trainingScore ?? driverRecord?.safetyScore ?? 0),
    completedCount,
    pendingCount,
  };
};

const DRIVER_TABS = [
  { key: "overview", label: "Overview" },
  { key: "expense_logs", label: "Expense Logs" },
  { key: "human_resources", label: "Human Resources" },
  SHOW_DRIVER_TRAININGS_TAB ? { key: "trainings", label: "Trainings" } : null,
].filter(Boolean);

const EXPENSE_LOG_CATEGORY_OPTIONS = [
  { label: "Fuel", value: "Fuel" },
  { label: "Toll", value: "Toll" },
  { label: "Misc", value: "Misc" },
];

const ATTENDANCE_CATEGORY_OPTIONS = [
  { label: "Present", value: "Present" },
  { label: "On Time", value: "On Time" },
  { label: "Late", value: "Late" },
];

const LEAVE_TYPE_OPTIONS = [
  { label: "Casual", value: "Casual" },
  { label: "Sick", value: "Sick" },
  { label: "Annual", value: "Annual" },
];

const LEAVE_CATEGORY_OPTIONS = [
  { label: "Pending", value: "Pending" },
  { label: "Approved", value: "Approved" },
];

const PAYROLL_STATUS_OPTIONS = [
  { label: "Pending", value: "Pending" },
  { label: "Paid", value: "Paid" },
];

const TRAINING_COMPLETION_OPTIONS = [
  { label: "Partial", value: "Partial" },
  { label: "Pending", value: "Pending" },
  { label: "Completed", value: "Completed" },
];

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const matchesNumberFilter = (sourceValue, filterType, filterValue) => {
  const left = Number(sourceValue);
  const right = Number(filterValue);

  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;

  switch (filterType) {
    case "greater_than":
      return left > right;
    case "greater_than_equal_to":
      return left >= right;
    case "less_than":
      return left < right;
    case "less_than_equal_to":
      return left <= right;
    case "equal_to":
    case "equals":
    default:
      return left === right;
  }
};

const matchesTextFilter = (sourceValue, filterType, filterValue) => {
  const source = normalizeText(sourceValue);
  const target = normalizeText(filterValue);

  switch (filterType) {
    case "equals":
    case "equal_to":
      return source === target;
    case "contains":
    default:
      return source.includes(target);
  }
};

const matchesFilterValue = (sourceValue, filterConfig) => {
  const filterType = filterConfig?.filter_type;
  const filterValue = filterConfig?.value;

  if (
    !filterType ||
    filterValue === undefined ||
    filterValue === null ||
    filterValue === "" ||
    (Array.isArray(filterValue) && filterValue.length === 0)
  ) {
    return true;
  }

  if (filterType === "one_of") {
    const allowedValues = Array.isArray(filterValue) ? filterValue : [filterValue];
    return allowedValues.some(
      (value) => normalizeText(value) === normalizeText(sourceValue)
    );
  }

  if (typeof sourceValue === "number") {
    return matchesNumberFilter(sourceValue, filterType, filterValue);
  }

  return matchesTextFilter(sourceValue, filterType, filterValue);
};

const renderExpenseCategory = (category) => {
  const normalizedCategory = normalizeText(category);

  if (!normalizedCategory) {
    return <span className="driver_expense_logs_category_text">&nbsp;</span>;
  }

  if (normalizedCategory === "fuel") {
    return (
      <span className="driver_expense_logs_badge driver_expense_logs_badge_fuel">
        Fuel
      </span>
    );
  }

  if (normalizedCategory === "toll") {
    return (
      <span className="driver_expense_logs_badge driver_expense_logs_badge_toll">
        Toll
      </span>
    );
  }

  if (normalizedCategory === "misc") {
    return (
      <span className="driver_expense_logs_badge driver_expense_logs_badge_misc">
        Misc
      </span>
    );
  }

  return <span className="driver_expense_logs_category_text">{category || "-"}</span>;
};

const renderHumanResourcesBadge = (value) => {
  const normalizedValue = normalizeText(value);

  if (
    normalizedValue === "present" ||
    normalizedValue === "on time" ||
    normalizedValue === "approved" ||
    normalizedValue === "paid"
  ) {
    return (
      <span className="driver_hr_badge driver_hr_badge_success">
        {value}
      </span>
    );
  }

  if (normalizedValue === "late" || normalizedValue === "pending") {
    return (
      <span className="driver_hr_badge driver_hr_badge_warning">
        {value}
      </span>
    );
  }

  return <span className="driver_expense_logs_category_text">{value || "-"}</span>;
};

const renderAttendanceCellText = (value, maxWidth = "180px") => {
  const text = String(value || "").trim();
  if (!text) return "-";

  return <MultiLineText text={text} maxWidth={maxWidth} />;
};

const renderTrainingCompletionBadge = (value) => {
  const normalizedValue = normalizeText(value);

  if (normalizedValue === "completed") {
    return (
      <span className="driver_training_completion_badge driver_training_completion_badge_completed">
        {value}
      </span>
    );
  }

  if (normalizedValue === "pending") {
    return (
      <span className="driver_training_completion_badge driver_training_completion_badge_pending">
        {value}
      </span>
    );
  }

  if (normalizedValue === "partial") {
    return (
      <span className="driver_training_completion_badge driver_training_completion_badge_partial">
        {value}
      </span>
    );
  }

  return <span className="driver_expense_logs_category_text">{value || "-"}</span>;
};

const renderTrainingProgress = (value) => {
  const progressPercent = Math.max(0, Math.min(100, Number(value || 0)));
  let fillClass = "driver_training_progress_fill_partial";

  if (progressPercent >= 100) {
    fillClass = "driver_training_progress_fill_completed";
  } else if (progressPercent <= 0) {
    fillClass = "driver_training_progress_fill_pending";
  }

  return (
    <div className="driver_training_progress_cell">
      <div className="driver_training_progress_track">
        <span
          className={`driver_training_progress_fill ${fillClass}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <strong>{`${progressPercent}%`}</strong>
    </div>
  );
};

const renderTrainingSummaryChip = (type, count, label) => {
  const isCompleted = type === "completed";

  return (
    <span
      className={`driver_training_summary_chip ${isCompleted
          ? "driver_training_summary_chip_completed"
          : "driver_training_summary_chip_pending"
        }`}
    >
      {isCompleted ? (
        <FaCheckCircle className="driver_training_summary_icon" />
      ) : (
        <FaExclamationTriangle className="driver_training_summary_icon" />
      )}
      <span>{`${count} - ${label}`}</span>
    </span>
  );
};

const filterTableRows = (rows, searchText, searchFilters, filterAccessors, searchFields) => {
  const normalizedSearchText = normalizeText(searchText);

  return rows.filter((row) => {
    const matchesSearch =
      normalizedSearchText.length === 0 ||
      searchFields.some((field) => normalizeText(row?.[field]).includes(normalizedSearchText));

    if (!matchesSearch) return false;

    return Object.entries(searchFilters || {}).every(([filterName, filterConfig]) => {
      const accessor = filterAccessors[filterName];
      if (!accessor) return true;
      return matchesFilterValue(accessor(row), filterConfig);
    });
  });
};

const createTableState = () => ({
  currentPage: 1,
  limit: 8,
  searchText: "",
  searchFilters: {},
});

const DriverDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canUpdate = hasPermission({
    permissionSlug: DriverSafetyPermission.Update,
    menuSlug: OperationsSubModules.DriverSafety,
    moduleSlug: AppMainModules.operations,
  });
  const location = useLocation();
  const { id } = useParams();
  const {
    getDriversPaginatedData,
    driverDetail,
    getDriverDetailLoading,
    driverHistoryData,
    driverHistoryLoading,
    commentOnDriverLoading,
    generateDriverPasswordLoading,
    updateDriverLoading,
    getDriverExpensesPaginatedData,
    getDriverExpensesPaginatedLoading,
    getDriverPayrollData,
    getDriverPayrollLoading,
    getDriverAttendanceData,
    getDriverAttendanceLoading,
    getDriverLeavesData,
    getDriverLeavesLoading,
  } = useSelector((state) => state.drivers || {});

  const { usersDropdownData = [] } = useSelector(
    (state) => state.dropdowns || {}
  );
  const profileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchUsersDropdown());
  }, [dispatch]);

  const userOptions = useMemo(() => {
    const list = Array.isArray(usersDropdownData) ? usersDropdownData : [];
    return list
      .map((user) => {
        const rawValue = user?.value ?? user?.id;
        const rawLabel =
          user?.label ??
          user?.name ??
          user?.user_name ??
          user?.username ??
          user?.email ??
          (rawValue ? `User #${rawValue}` : "");
        return {
          ...user,
          label: String(rawLabel || "").trim(),
          value: rawValue,
        };
      })
      .filter((opt) => opt.value !== undefined && opt.value !== null && opt.value !== "");
  }, [usersDropdownData]);
  const [activeTab, setActiveTab] = useState("overview");
  const [driverRecord, setDriverRecord] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePreviewUrl, setProfilePreviewUrl] = useState("");
  const [exportCardLoading, setExportCardLoading] = useState(false);
  const [expenseCurrentPage, setExpenseCurrentPage] = useState(1);
  const [expenseLimit, setExpenseLimit] = useState(8);
  const [expenseSearchText, setExpenseSearchText] = useState("");
  const [expenseSearchFilters, setExpenseSearchFilters] = useState({});
  const [attachmentPreviewFiles, setAttachmentPreviewFiles] = useState([]);
  const [attendanceTable, setAttendanceTable] = useState(createTableState);
  const [allowances, setAllowances] = useState([]);
  const [allowanceForm, setAllowanceForm] = useState(EMPTY_ALLOWANCE_FORM);
  const [allowanceSaving, setAllowanceSaving] = useState(false);
  const [allowanceErrors, setAllowanceErrors] = useState({});
  const [arrearsForm, setArrearsForm] = useState(EMPTY_ARREARS_FORM);
  const [arrearsErrors, setArrearsErrors] = useState({});

  const openAttachmentViewer = useCallback((files = []) => {
    const nextFiles = Array.isArray(files) ? files : [];
    if (!nextFiles.length) {
      errorNotify("No attachments found");
      return;
    }
    setAttachmentPreviewFiles(nextFiles);
  }, []);
  const [leaveTable, setLeaveTable] = useState(createTableState);
  const [payrollTable, setPayrollTable] = useState(createTableState);
  const [trainingTable, setTrainingTable] = useState(createTableState);

  useEffect(() => {
    if (!DRIVER_TABS.some((tab) => tab.key === activeTab)) {
      setActiveTab("overview");
    }
  }, [activeTab]);

  useEffect(() => {
    if (id) {
      dispatch(fetchDriverDetail(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const stateDriver = location.state?.driver
      ? normalizeDriverRecord(location.state.driver)
      : null;
    const apiDetailDriver = driverDetail ? normalizeDriverRecord(driverDetail) : null;
    const matchedApiDriver = Array.isArray(getDriversPaginatedData?.results)
      ? getDriversPaginatedData.results.find(
        (driver) =>
          String(driver?.id || "") === String(id || "") ||
          String(driver?.driver_info_id || "") === String(id || "") ||
          String(driver?.employee_id || "") === String(id || "")
      )
      : null;
    const nextDriver =
      apiDetailDriver ||
      stateDriver ||
      (matchedApiDriver ? normalizeDriverRecord(matchedApiDriver) : null);
    setDriverRecord(nextDriver);
    setProfilePreviewUrl((previousUrl) => {
      if (String(previousUrl || "").startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }
      return nextDriver?.image || "";
    });
    setProfilePicture(null);
  }, [driverDetail, getDriversPaginatedData, id, location.state]);

  useEffect(() => {
    return () => {
      if (String(profilePreviewUrl || "").startsWith("blob:")) {
        URL.revokeObjectURL(profilePreviewUrl);
      }
    };
  }, [profilePreviewUrl]);

  const initialValues = useMemo(
    () => buildDriverFormValues(driverRecord, driverRecord?.employeeNumber, ""),
    [driverRecord]
  );

  const driverHistoryId = Number(
    driverRecord?.id ??
    driverDetail?.id ??
    location.state?.driver?.id ??
    id ??
    0
  );

  useEffect(() => {
    if (!driverHistoryId) return;
    apiService.getDriverAllowances(driverHistoryId).then((res) => {
      setAllowances((res?.data || []).map((a) => ({ ...a, _localId: a.id })));
    }).catch(() => {});
  }, [driverHistoryId]);

  const refreshAllowances = async () => {
    const refreshed = await apiService.getDriverAllowances(driverHistoryId);
    setAllowances((refreshed?.data || []).map((a) => ({ ...a, _localId: a.id })));
  };

  const saveDriverItem = async ({ editingId, allowance_type, amount, frequency }) => {
    const entry = {
      id: editingId && typeof editingId === "number" ? editingId : undefined,
      allowance_type,
      amount: Number(amount),
      frequency,
    };
    await apiService.saveDriverAllowances(driverHistoryId, [entry]);
    await refreshAllowances();
  };

  const handleAddAllowance = async () => {
    const { editingId, allowanceType, amount, frequency } = allowanceForm;
    const errs = {};
    if (!allowanceType) errs.allowanceType = "Please select an allowance type";
    if (!amount) errs.amount = "Please enter the amount";
    if (!frequency) errs.frequency = "Please select a frequency";
    if (Object.keys(errs).length) { setAllowanceErrors(errs); return; }
    setAllowanceErrors({});
    setAllowanceSaving(true);
    try {
      await saveDriverItem({ editingId, allowance_type: allowanceType, amount, frequency });
      setAllowanceForm(EMPTY_ALLOWANCE_FORM);
      setAllowanceErrors({});
      successNotify(editingId ? "Allowance updated" : "Allowance added");
    } catch (err) {
      errorNotify(err || "Failed to save allowance");
    } finally {
      setAllowanceSaving(false);
    }
  };

  const handleEditAllowance = (item) => {
    setArrearsForm(EMPTY_ARREARS_FORM);
    setAllowanceForm({
      editingId: item.id,
      allowanceType: item.allowance_type,
      amount: String(item.amount),
      frequency: item.frequency,
    });
  };

  const handleDeleteAllowance = async (item) => {
    try {
      await apiService.deleteDriverAllowance(item.id);
      setAllowances((prev) => prev.filter((a) => a.id !== item.id));
      successNotify("Deleted");
    } catch (err) {
      errorNotify(err || "Failed to delete");
    }
  };

  // ── Arrears handlers ──────────────────────────────────────────────────────
  const handleSaveArrears = async () => {
    const { editingId, direction, amount, frequency } = arrearsForm;
    const errs = {};
    if (!direction) errs.direction = "Please select a direction";
    if (!amount) errs.amount = "Please enter the amount";
    if (!frequency) errs.frequency = "Please select a frequency";
    if (Object.keys(errs).length) { setArrearsErrors(errs); return; }
    setArrearsErrors({});
    setAllowanceSaving(true);
    try {
      await saveDriverItem({ editingId, allowance_type: direction, amount, frequency });
      setArrearsForm(EMPTY_ARREARS_FORM);
      successNotify(editingId ? "Arrears updated" : "Arrears added");
    } catch (err) {
      errorNotify(err || "Failed to save arrears");
    } finally {
      setAllowanceSaving(false);
    }
  };

  const handleEditArrears = (item) => {
    setAllowanceForm(EMPTY_ALLOWANCE_FORM);
    setArrearsForm({
      editingId: item.id,
      direction: item.allowance_type,
      amount: String(item.amount),
      frequency: item.frequency,
    });
  };

  const canUseDriverHistoryApi =
    Number.isFinite(driverHistoryId) && driverHistoryId > 0;

  const resolvedHistoryItems = useMemo(() => {
    if (Array.isArray(driverHistoryData)) return driverHistoryData;
    if (Array.isArray(driverRecord?.historyItems)) return driverRecord.historyItems;
    return [];
  }, [driverHistoryData, driverRecord]);

  const handleFetchDriverHistory = useCallback(
    async (historyType = "all") => {
      if (!canUseDriverHistoryApi) return;

      await dispatch(
        fetchDriverHistory({
          driver_info_id: driverHistoryId,
          history_type: historyType,
        })
      ).unwrap();
    },
    [canUseDriverHistoryApi, dispatch, driverHistoryId]
  );

  const handleSubmitDriverComment = useCallback(
    async ({ comment, parent_id, mention_user_ids }) => {
      if (!canUseDriverHistoryApi) return;

      await dispatch(
        commentOnDriver({
          driver_info_id: driverHistoryId,
          comment,
          parent_id,
          mention_user_ids,
        })
      ).unwrap();
    },
    [canUseDriverHistoryApi, dispatch, driverHistoryId]
  );

  const handleGenerateDriverPassword = useCallback(
    async (setFieldValue) => {
      try {
        const response = await dispatch(generateDriverPassword()).unwrap();
        const generatedPassword = String(response?.data ?? response ?? "").trim();

        if (!generatedPassword) {
          throw "Driver password generation failed";
        }

        setFieldValue("appAuthCode", generatedPassword);
      } catch (error) {
        errorNotify(error);
      }
    },
    [dispatch]
  );

  const handleExportDriverCard = useCallback(
    async (values = {}) => {
      const driverId = Number(driverRecord?.id || driverHistoryId || id || 0);
      const driverImage = String(driverRecord?.image || "").trim();
      const payload = {
        driver_id: driverId,
        employee_id: String(values?.employeeNumber || driverRecord?.employeeNumber || "").trim(),
        name: String(values?.driverName || driverRecord?.driverName || "").trim(),
        cnic: String(values?.cnicNumber || driverRecord?.cnicNumber || "").trim(),
        phone: formatIdCardPhone(values?.contact || driverRecord?.contactNumber),
      };

      if (driverImage && !driverImage.startsWith("blob:")) {
        payload.image = driverImage;
      }

      setExportCardLoading(true);
      try {
        const response = await apiService.generateDriverIdCard(payload);

        let cardUrl = "";
        if (response) {
          if (typeof response === "string") {
            cardUrl = response.trim();
          } else if (typeof response?.data === "string") {
            cardUrl = response.data.trim();
          } else if (response?.data && typeof response.data === "object" && typeof response.data.url === "string") {
            cardUrl = response.data.url.trim();
          } else if (response?.data && typeof response.data === "object" && typeof response.data.card_url === "string") {
            cardUrl = response.data.card_url.trim();
          } else if (typeof response?.url === "string") {
            cardUrl = response.url.trim();
          } else if (typeof response?.card_url === "string") {
            cardUrl = response.card_url.trim();
          } else if (typeof response?.cardUrl === "string") {
            cardUrl = response.cardUrl.trim();
          }
        }

        if (!cardUrl) {
          throw "Driver ID card link was not returned";
        }

        const fileExtension = getFileExtensionFromUrl(cardUrl);
        const fileName = `${sanitizeDownloadName(payload.name)}-id-card.${fileExtension}`;
        await downloadRemoteImage(cardUrl, fileName);
        successNotify(response?.message || "Driver ID card downloaded successfully");
      } catch (error) {
        errorNotify(error);
      } finally {
        setExportCardLoading(false);
      }
    },
    [driverHistoryId, driverRecord, id]
  );

  useEffect(() => {
    const driverId = Number(driverRecord?.id || id || 0);
    if (!driverId || activeTab !== "expense_logs") return;

    const targetUserId =
      driverRecord?.userId ||
      driverRecord?.user_id ||
      driverDetail?.user_id ||
      driverDetail?.user?.id ||
      location.state?.driver?.userId ||
      location.state?.driver?.user_id ||
      location.state?.driver?.user?.id;

    if (!targetUserId) return;

    const payload = {
      driver_id: Number(targetUserId),
      page: expenseCurrentPage,
      limit: expenseLimit,
    };

    if (expenseSearchText) {
      payload.search_text = expenseSearchText;
    }

    if (expenseSearchFilters && Object.keys(expenseSearchFilters).length > 0) {
      payload.searchFilters = expenseSearchFilters;
    }

    dispatch(fetchDriverExpensesPaginated(payload));
  }, [
    dispatch,
    driverRecord?.id,
    driverRecord?.userId,
    driverRecord?.user_id,
    driverDetail?.user_id,
    driverDetail?.user?.id,
    location.state?.driver,
    id,
    activeTab,
    expenseCurrentPage,
    expenseLimit,
    expenseSearchText,
    expenseSearchFilters,
  ]);

  useEffect(() => {
    const driverId = Number(driverRecord?.id || id || 0);
    if (!driverId || activeTab !== "human_resources") return;

    dispatch(fetchDriverPayroll({ drive_id: driverId }));

    const targetUserId =
      driverRecord?.userId ||
      driverRecord?.user_id ||
      driverDetail?.user_id ||
      driverDetail?.user?.id ||
      location.state?.driver?.userId ||
      location.state?.driver?.user_id ||
      location.state?.driver?.user?.id;

    if (targetUserId) {
      dispatch(fetchDriverAttendance({ drive_id: Number(targetUserId) }));
      dispatch(fetchDriverLeaves({ drive_id: Number(targetUserId) }));
    }
  }, [
    activeTab,
    dispatch,
    driverRecord?.id,
    driverRecord?.userId,
    driverRecord?.user_id,
    driverDetail?.user_id,
    driverDetail?.user?.id,
    location.state?.driver,
    id,
  ]);

  const paginatedExpenseLogs = useMemo(() => {
    const results = getDriverExpensesPaginatedData?.results || [];
    return normalizeArray(results).map((expenseLog, index) =>
      normalizeExpenseLogItem(expenseLog, index)
    );
  }, [getDriverExpensesPaginatedData]);

  useEffect(() => {
    setExpenseCurrentPage(1);
  }, [expenseSearchText, expenseSearchFilters]);

  const expenseTotalItems = Number(getDriverExpensesPaginatedData?.meta?.totalItems || 0);
  const expenseTotalPages = Number(getDriverExpensesPaginatedData?.meta?.totalPages || 1);

  useEffect(() => {
    if (expenseCurrentPage > expenseTotalPages) {
      setExpenseCurrentPage(expenseTotalPages);
    }
  }, [expenseCurrentPage, expenseTotalPages]);

  const expenseColumns = useMemo(
    () => [
      {
        name: "Expense ID",
        selector: (row) => <span className="hyperlink">{row.expenseId}</span>,
        tableColumn: true,
        grow: 1.2,
      },
      {
        name: "Category",
        selector: (row) => renderExpenseCategory(row.category),
        tableColumn: true,
        options: EXPENSE_LOG_CATEGORY_OPTIONS,
        grow: 1.2,
      },
      {
        name: "Trip ID",
        selector: (row) => <span className="hyperlink">{row.tripId}</span>,
        tableColumn: true,
        grow: 1.1,
      },
      {
        name: "Amount (PKR)",
        selector: (row) => formatDriverAmount(row.amount),
        tableColumn: true,
        grow: 1.2,
      },
      {
        name: "Created Date",
        selector: (row) => row.createdDate,
        tableColumn: true,
        grow: 1.2,
      },
      {
        name: "Attachments",
        selector: (row) => {
          const filesCount = Array.isArray(row.files) ? row.files.length : 0;
          return filesCount > 0 ? (
            <button
              type="button"
              className="d-flex align-items-center gap-1"
              style={{
                color: "#3d18c3",
                border: "none",
                background: "transparent",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={(event) => {
                event.stopPropagation();
                openAttachmentViewer(row.files);
              }}
              title="View attachments"
            >
              <FiPaperclip /> {filesCount}
            </button>
          ) : (
            "-"
          );
        },
        tableColumn: true,
        grow: 1.0,
      },
    ],
    [openAttachmentViewer]
  );

  const attendanceLogs = useMemo(() => {
    if (getDriverAttendanceData) {
      return getDriverAttendanceLogs({
        attendanceLogs: normalizeArray(getDriverAttendanceData?.results),
      });
    }

    return getDriverAttendanceLogs(driverRecord);
  }, [driverRecord, getDriverAttendanceData]);
  const leaveLogs = useMemo(() => {
    if (getDriverLeavesData) {
      return getDriverLeaveLogs({
        leaveLogs: normalizeArray(getDriverLeavesData?.results),
      });
    }

    return getDriverLeaveLogs(driverRecord);
  }, [driverRecord, getDriverLeavesData]);
  const payrollLogs = useMemo(() => {
    if (getDriverPayrollData) {
      return getDriverPayrollLogs({
        payrollLogs: normalizeArray(getDriverPayrollData?.results),
      });
    }

    return getDriverPayrollLogs(driverRecord);
  }, [driverRecord, getDriverPayrollData]);

  const attendanceFilterAccessors = useMemo(
    () => ({
      serialNoFilter: (row) => row.serialNo,
      attendanceDateFilter: (row) => row.attendanceDate,
      locationFilter: (row) => row.location,
      geoCoordinatesFilter: (row) => row.geoCoordinates,
      timeInFilter: (row) => row.timeIn,
      categoryFilter: (row) => row.category,
    }),
    []
  );

  const leaveFilterAccessors = useMemo(
    () => ({
      serialNoFilter: (row) => row.serialNo,
      leaveTypeFilter: (row) => row.leaveType,
      durationFilter: (row) => row.duration,
      fromTillFilter: (row) => row.fromTill,
      reasonFilter: (row) => row.reason,
      categoryFilter: (row) => row.category,
    }),
    []
  );

  const payrollFilterAccessors = useMemo(
    () => ({
      paymentIdFilter: (row) => row.paymentId,
      monthFilter: (row) => row.month,
      baseSalaryFilter: (row) => row.baseSalary,
      allowanceFilter: (row) => row.allowance,
      deductionFilter: (row) => row.deduction,
      statusFilter: (row) => row.status,
    }),
    []
  );

  useEffect(() => {
    setAttendanceTable((previous) => ({ ...previous, currentPage: 1 }));
  }, [attendanceTable.searchText, attendanceTable.searchFilters]);

  useEffect(() => {
    setLeaveTable((previous) => ({ ...previous, currentPage: 1 }));
  }, [leaveTable.searchText, leaveTable.searchFilters]);

  useEffect(() => {
    setPayrollTable((previous) => ({ ...previous, currentPage: 1 }));
  }, [payrollTable.searchText, payrollTable.searchFilters]);

  const filteredAttendanceLogs = useMemo(
    () =>
      filterTableRows(
        attendanceLogs,
        attendanceTable.searchText,
        attendanceTable.searchFilters,
        attendanceFilterAccessors,
        ["serialNo", "attendanceDate", "location", "geoCoordinates", "timeIn", "category"]
      ),
    [
      attendanceFilterAccessors,
      attendanceLogs,
      attendanceTable.searchFilters,
      attendanceTable.searchText,
    ]
  );

  const filteredLeaveLogs = useMemo(
    () =>
      filterTableRows(
        leaveLogs,
        leaveTable.searchText,
        leaveTable.searchFilters,
        leaveFilterAccessors,
        ["serialNo", "leaveType", "duration", "fromTill", "reason", "category"]
      ),
    [leaveFilterAccessors, leaveLogs, leaveTable.searchFilters, leaveTable.searchText]
  );

  const filteredPayrollLogs = useMemo(
    () =>
      filterTableRows(
        payrollLogs,
        payrollTable.searchText,
        payrollTable.searchFilters,
        payrollFilterAccessors,
        ["paymentId", "month", "baseSalary", "allowance", "deduction", "status"]
      ),
    [
      payrollFilterAccessors,
      payrollLogs,
      payrollTable.searchFilters,
      payrollTable.searchText,
    ]
  );

  const attendanceTotalPages = Math.max(
    1,
    Math.ceil(filteredAttendanceLogs.length / Math.max(attendanceTable.limit, 1))
  );
  const leaveTotalPages = Math.max(
    1,
    Math.ceil(filteredLeaveLogs.length / Math.max(leaveTable.limit, 1))
  );
  const payrollTotalPages = Math.max(
    1,
    Math.ceil(filteredPayrollLogs.length / Math.max(payrollTable.limit, 1))
  );

  useEffect(() => {
    if (attendanceTable.currentPage > attendanceTotalPages) {
      setAttendanceTable((previous) => ({
        ...previous,
        currentPage: attendanceTotalPages,
      }));
    }
  }, [attendanceTable.currentPage, attendanceTotalPages]);

  useEffect(() => {
    if (leaveTable.currentPage > leaveTotalPages) {
      setLeaveTable((previous) => ({
        ...previous,
        currentPage: leaveTotalPages,
      }));
    }
  }, [leaveTable.currentPage, leaveTotalPages]);

  useEffect(() => {
    if (payrollTable.currentPage > payrollTotalPages) {
      setPayrollTable((previous) => ({
        ...previous,
        currentPage: payrollTotalPages,
      }));
    }
  }, [payrollTable.currentPage, payrollTotalPages]);

  const paginatedAttendanceLogs = useMemo(() => {
    const startIndex = (attendanceTable.currentPage - 1) * attendanceTable.limit;
    return filteredAttendanceLogs.slice(startIndex, startIndex + attendanceTable.limit);
  }, [attendanceTable.currentPage, attendanceTable.limit, filteredAttendanceLogs]);

  const paginatedLeaveLogs = useMemo(() => {
    const startIndex = (leaveTable.currentPage - 1) * leaveTable.limit;
    return filteredLeaveLogs.slice(startIndex, startIndex + leaveTable.limit);
  }, [filteredLeaveLogs, leaveTable.currentPage, leaveTable.limit]);

  const paginatedPayrollLogs = useMemo(() => {
    const startIndex = (payrollTable.currentPage - 1) * payrollTable.limit;
    return filteredPayrollLogs.slice(startIndex, startIndex + payrollTable.limit);
  }, [filteredPayrollLogs, payrollTable.currentPage, payrollTable.limit]);

  const attendanceColumns = useMemo(
    () => [
      {
        name: "Serial No",
        selector: (row) => row.serialNo,
        tableColumn: true,
        filterName: "serialNoFilter",
        type: "number",
        grow: 0.9,
      },
      {
        name: "Attendance Date",
        selector: (row) => row.attendanceDate || "-",
        tableColumn: true,
        filterName: "attendanceDateFilter",
        type: "text",
        grow: 1.2,
      },
      {
        name: "Location",
        selector: (row) => renderAttendanceCellText(row.location, "240px"),
        tableColumn: true,
        filterName: "locationFilter",
        type: "text",
        grow: 1.7,
      },
      {
        name: "Time IN",
        selector: (row) => row.timeIn || "-",
        tableColumn: true,
        filterName: "timeInFilter",
        type: "text",
        grow: 1,
      },
      {
        name: "Category",
        selector: (row) => renderHumanResourcesBadge(row.category),
        tableColumn: true,
        filterName: "categoryFilter",
        type: "text",
        options: ATTENDANCE_CATEGORY_OPTIONS,
        grow: 1,
      },
    ],
    []
  );

  const leaveColumns = useMemo(
    () => [
      {
        name: "Serial No",
        selector: (row) => row.serialNo,
        tableColumn: true,
        filterName: "serialNoFilter",
        type: "number",
        grow: 0.9,
      },
      {
        name: "Leave Type",
        selector: (row) => row.leaveType || "-",
        tableColumn: true,
        filterName: "leaveTypeFilter",
        type: "text",
        options: LEAVE_TYPE_OPTIONS,
        grow: 1.1,
      },
      {
        name: "Duration",
        selector: (row) => row.duration || "-",
        tableColumn: true,
        filterName: "durationFilter",
        type: "text",
        grow: 1,
      },
      {
        name: "From / Till",
        selector: (row) => row.fromTill || "-",
        tableColumn: true,
        filterName: "fromTillFilter",
        type: "text",
        grow: 1.5,
      },
      {
        name: "Reason",
        selector: (row) => renderAttendanceCellText(row.reason, "220px"),
        tableColumn: true,
        filterName: "reasonFilter",
        type: "text",
        grow: 1.3,
      },
      {
        name: "Category",
        selector: (row) => renderHumanResourcesBadge(row.category),
        tableColumn: true,
        filterName: "categoryFilter",
        type: "text",
        options: LEAVE_CATEGORY_OPTIONS,
        grow: 1,
      },
    ],
    []
  );

  const payrollColumns = useMemo(
    () => [
      {
        name: "Payment ID",
        selector: (row) => <span className="hyperlink">{row.paymentId}</span>,
        tableColumn: true,
        filterName: "paymentIdFilter",
        type: "text",
        grow: 1.2,
      },
      {
        name: "Month",
        selector: (row) => row.month,
        tableColumn: true,
        filterName: "monthFilter",
        type: "text",
        grow: 1,
      },
      {
        name: "Base Salary",
        selector: (row) => formatDriverAmount(row.baseSalary),
        tableColumn: true,
        filterName: "baseSalaryFilter",
        type: "number",
        grow: 1.1,
      },
      {
        name: "Allowance",
        selector: (row) => formatDriverAmount(row.allowance),
        tableColumn: true,
        filterName: "allowanceFilter",
        type: "number",
        grow: 1,
      },
      {
        name: "Deduction",
        selector: (row) => formatDriverAmount(row.deduction),
        tableColumn: true,
        filterName: "deductionFilter",
        type: "number",
        grow: 1,
      },
      {
        name: "Status",
        selector: (row) => renderHumanResourcesBadge(row.status),
        tableColumn: true,
        filterName: "statusFilter",
        type: "text",
        options: PAYROLL_STATUS_OPTIONS,
        grow: 1,
      },
    ],
    []
  );

  const trainingLogs = useMemo(() => getDriverTrainingLogs(driverRecord), [driverRecord]);
  const trainingSummary = useMemo(
    () => getDriverTrainingSummary(driverRecord, trainingLogs),
    [driverRecord, trainingLogs]
  );

  const trainingFilterAccessors = useMemo(
    () => ({
      videoTitleFilter: (row) => row.videoTitle,
      durationFilter: (row) => row.duration,
      watchTimeFilter: (row) => row.watchTime,
      completionFilter: (row) => row.completion,
      progressPercentFilter: (row) => row.progressPercent,
      lastActivityFilter: (row) => row.lastActivity,
    }),
    []
  );

  useEffect(() => {
    setTrainingTable((previous) => ({ ...previous, currentPage: 1 }));
  }, [trainingTable.searchText, trainingTable.searchFilters]);

  const filteredTrainingLogs = useMemo(
    () =>
      filterTableRows(
        trainingLogs,
        trainingTable.searchText,
        trainingTable.searchFilters,
        trainingFilterAccessors,
        ["videoTitle", "duration", "watchTime", "completion", "progressPercent", "lastActivity"]
      ),
    [
      trainingFilterAccessors,
      trainingLogs,
      trainingTable.searchFilters,
      trainingTable.searchText,
    ]
  );

  const trainingTotalPages = Math.max(
    1,
    Math.ceil(filteredTrainingLogs.length / Math.max(trainingTable.limit, 1))
  );

  useEffect(() => {
    if (trainingTable.currentPage > trainingTotalPages) {
      setTrainingTable((previous) => ({
        ...previous,
        currentPage: trainingTotalPages,
      }));
    }
  }, [trainingTable.currentPage, trainingTotalPages]);

  const paginatedTrainingLogs = useMemo(() => {
    const startIndex = (trainingTable.currentPage - 1) * trainingTable.limit;
    return filteredTrainingLogs.slice(startIndex, startIndex + trainingTable.limit);
  }, [filteredTrainingLogs, trainingTable.currentPage, trainingTable.limit]);

  const trainingColumns = useMemo(
    () => [
      {
        name: "Video Title",
        selector: (row) => row.videoTitle,
        tableColumn: true,
        filterName: "videoTitleFilter",
        type: "text",
        grow: 2.4,
      },
      {
        name: "Duration",
        selector: (row) => row.duration,
        tableColumn: true,
        filterName: "durationFilter",
        type: "text",
        grow: 1,
      },
      {
        name: "Watch Time",
        selector: (row) => row.watchTime,
        tableColumn: true,
        filterName: "watchTimeFilter",
        type: "text",
        grow: 1.1,
      },
      {
        name: "Completion",
        selector: (row) => renderTrainingCompletionBadge(row.completion),
        tableColumn: true,
        filterName: "completionFilter",
        type: "text",
        options: TRAINING_COMPLETION_OPTIONS,
        grow: 1.1,
      },
      {
        name: "Status",
        selector: (row) => renderTrainingProgress(row.progressPercent),
        tableColumn: true,
        filterName: "progressPercentFilter",
        type: "number",
        grow: 1.35,
      },
      {
        name: "Last Activity",
        selector: (row) => row.lastActivity,
        tableColumn: true,
        filterName: "lastActivityFilter",
        type: "text",
        grow: 1,
      },
    ],
    []
  );

  const renderAssignedVehicleCard = () => {
    const assignedVehicles = normalizeArray(driverRecord?.assignedVehicles);
    const assignedDriverId = String(driverRecord?.driverId || "").trim();

    return (
      <div className="driver_detail_assignment_card">
        <div className="driver_form_section_header">
          <h3>Assigned Vehicle</h3>
          <p>Vehicle currently linked with this driver role.</p>
        </div>

        {assignedVehicles.length ? (
          <div className="driver_detail_assignment_list">
            {assignedVehicles.map((vehicle) => (
              <div
                className="driver_detail_assignment_item"
                key={`${vehicle.role}-${vehicle.id || vehicle.label}`}
              >
                <div>
                  <span className="driver_detail_assignment_role">{vehicle.role}</span>
                  <strong>{assignedDriverId || vehicle.label}</strong>
                </div>
                <div className="driver_detail_assignment_meta">
                  {vehicle.odm ? <span>ODM: {vehicle.odm}</span> : null}
                  {vehicle.imeino ? <span>IMEI: {vehicle.imeino}</span> : null}
                  {vehicle.currentFuel ? <span>Fuel: {vehicle.currentFuel}</span> : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="driver_detail_assignment_empty">No vehicle assigned</div>
        )}
      </div>
    );
  };

  const updateProfilePreview = (nextUrl) => {
    setProfilePreviewUrl((previousUrl) => {
      if (String(previousUrl || "").startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }
      return nextUrl;
    });
  };

  const handleProfilePictureChange = (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;

    if (!String(selectedFile.type || "").startsWith("image/")) {
      errorNotify("Only image files are allowed for profile picture");
      event.target.value = "";
      return;
    }

    if (selectedFile.size > PROFILE_PICTURE_MAX_SIZE_BYTES) {
      errorNotify("Profile picture must be under 2 MB");
      event.target.value = "";
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setProfilePicture(selectedFile);
    updateProfilePreview(objectUrl);
    event.target.value = "";
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (!canUpdate) {
        errorNotify("You do not have permission to update drivers.");
        setSubmitting(false);
        return;
      }

      let resolvedProfileImage = profilePreviewUrl;

      if (profilePicture) {
        const formData = new FormData();
        formData.append("media", profilePicture);
        formData.append("destination", "driver-safety");

        const uploadResponse = await dispatch(uploadFiles(formData)).unwrap();
        const uploadedImagePath = resolveUploadedDriverImagePath(uploadResponse);

        if (!uploadedImagePath) {
          throw "Profile picture upload failed";
        }

        resolvedProfileImage = uploadedImagePath;
      }

      const updatedDriver = buildDriverRecordFromValues({
        values,
        currentDriver: driverRecord,
        profileImage: resolvedProfileImage,
        historyItems: resolvedHistoryItems,
      });

      const currentPayload = buildDriverUpdatePayload(updatedDriver);
      const initialPayload = buildDriverUpdatePayload(driverRecord);
      const updatedKeys = DRIVER_UPDATE_TRACKED_KEYS.filter(
        (key) =>
          !areDriverPayloadValuesEqual(currentPayload[key], initialPayload[key])
      );

      const payload = {
        ...currentPayload,
        updatedKeys,
      };

      await dispatch(updateDriver(payload)).unwrap();
      setDriverRecord(updatedDriver);
      setProfilePicture(null);

      successNotify("Driver details updated successfully");
      navigate("/driver-safety");
    } catch (error) {
      errorNotify(getNotifyMessage(error, "Failed to update driver details"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!driverRecord) {
    return (
      <div className="assets_vehicle_details_page driver_details_page">
        <Breadcrumbs values={["Operations", "Driver", "Driver Detail"]} />

        <div className="access_view">
          <h2>Driver Detail</h2>
        </div>

        <div className="trip_information_main_div driver_form_shell">
          <div className="driver_form_section">
            <div className="driver_form_section_header">
              <h3>Driver not found</h3>
              <p>
                {getDriverDetailLoading
                  ? "Loading driver detail..."
                  : "The selected driver record is unavailable in the current data."}
              </p>
            </div>

            <button
              type="button"
              className="cancel_btn"
              onClick={() => navigate("/driver-safety")}
            >
              Back to Driver
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={driverFormSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setValues,
        setTouched,
        validateForm,
      }) => {
        const costSummary = driverRecord?.costSummary || {};
        const totalNetSalary = Math.max(
          Number(values.baseSalary || 0) - Number(costSummary?.deductions || 0),
          0
        );
        const showExpenseLogsTab = activeTab === "expense_logs";
        const showHumanResourcesTab = activeTab === "human_resources";
        const showTrainingsTab = activeTab === "trainings";

        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function"
              ? updater(values.attachments || [])
              : updater;
          setFieldValue("attachments", nextFiles);
        };

        const handleSaveDriverDetails = async () => {
          const validationErrors = await validateForm();

          if (Object.keys(validationErrors || {}).length) {
            setTouched(buildTouchedFromErrors(validationErrors), true);
            setActiveTab("overview");
            return;
          }

          handleSubmit();
        };

        const applySearchFilters = (setTableState, updater) => {
          setTableState((previous) => ({
            ...previous,
            searchFilters:
              typeof updater === "function"
                ? updater(previous.searchFilters || {})
                : updater,
          }));
        };

        const renderHumanResourcesSection = ({
          title,
          columns,
          data,
          tableState,
          setTableState,
          totalItems,
          totalPages,
          tableClassName,
          loading = false,
        }) => (
          <div className="driver_form_section driver_hr_section">
            <div className="driver_form_section_header">
              <h3>{title}</h3>
              <p>Main information for this part record.</p>
            </div>

            <NewTableComponent
              columns={columns}
              data={data}
              currentPage={tableState.currentPage}
              setCurrentPage={(value) =>
                setTableState((previous) => ({ ...previous, currentPage: value }))
              }
              limit={tableState.limit}
              setLimit={(value) =>
                setTableState((previous) => ({
                  ...previous,
                  limit: value,
                  currentPage: 1,
                }))
              }
              totalItems={totalItems}
              totalPages={totalPages}
              onSearch={(value) =>
                setTableState((previous) => ({
                  ...previous,
                  searchText: value,
                }))
              }
              searchValue={tableState.searchText}
              setSearchFilters={(updater) => applySearchFilters(setTableState, updater)}
              searchFilters={tableState.searchFilters}
              loading={loading}
              paginationClassName="inline_pagination"
              tableClassName={tableClassName}
              limitOptions={[8, 16, 24]}
            />
          </div>
        );

        const renderTrainingsTabContent = () => (
          <div className="driver_training_content">
            <div className="driver_training_summary_card">
              <div className="driver_training_summary_row">
                <div className="driver_training_summary_info">
                  <h3>Driver Score</h3>

                  <div className="driver_training_summary_chips">
                    {renderTrainingSummaryChip(
                      "pending",
                      trainingSummary.pendingCount,
                      "Pending Trainings"
                    )}
                    {renderTrainingSummaryChip(
                      "completed",
                      trainingSummary.completedCount,
                      "Completed"
                    )}
                  </div>
                </div>

                <div className="driver_training_score_circle">
                  <span>{trainingSummary.score}</span>
                </div>
              </div>

              <button type="button" className="driver_training_trend_toggle">
                <MdKeyboardArrowDown />
                <span>Click to View 2-week trend</span>
              </button>
            </div>

            <div className="driver_training_table_panel">
              <NewTableComponent
                columns={trainingColumns}
                data={paginatedTrainingLogs}
                currentPage={trainingTable.currentPage}
                setCurrentPage={(value) =>
                  setTrainingTable((previous) => ({ ...previous, currentPage: value }))
                }
                limit={trainingTable.limit}
                setLimit={(value) =>
                  setTrainingTable((previous) => ({
                    ...previous,
                    limit: value,
                    currentPage: 1,
                  }))
                }
                totalItems={filteredTrainingLogs.length}
                totalPages={trainingTotalPages}
                onSearch={(value) =>
                  setTrainingTable((previous) => ({
                    ...previous,
                    searchText: value,
                  }))
                }
                searchValue={trainingTable.searchText}
                setSearchFilters={(updater) => applySearchFilters(setTrainingTable, updater)}
                searchFilters={trainingTable.searchFilters}
                paginationClassName="inline_pagination"
                tableClassName="driver_training_table"
                limitOptions={[8, 16, 24]}
              />
            </div>
          </div>
        );

        return (
          <div className="assets_vehicle_details_page driver_details_page">
            <Breadcrumbs values={["Operations", "Driver", "Driver Detail"]} />

            <div className="page_header access_view">
              <h2>Driver Detail</h2>

              <div className="d-flex align-items-center gap-2 driver_details_header_actions">
                <button
                  type="button"
                  className="export_btn driver_details_export_btn"
                  onClick={() => handleExportDriverCard(values)}
                  disabled={
                    exportCardLoading ||
                    !String(values?.employeeNumber || driverRecord?.employeeNumber || "").trim() ||
                    !String(values?.driverName || driverRecord?.driverName || "").trim() ||
                    !String(values?.cnicNumber || driverRecord?.cnicNumber || "").trim() ||
                    !formatIdCardPhone(values?.contact || driverRecord?.contactNumber)
                  }
                >
                  {downloadIcon} {exportCardLoading ? "Exporting..." : "Export Card"}
                </button>
                <Button
                  cancelBtn={() => navigate(-1)}
                  loading={isSubmitting || updateDriverLoading}
                  disabled={isSubmitting || updateDriverLoading}
                  submitText={
                    isSubmitting || updateDriverLoading ? "Saving..." : "Save Details"
                  }
                  type="button"
                  shouldRow={false}
                  isShowSubmitBtn={canUpdate}
                  submitBtn={handleSaveDriverDetails}
                />
              </div>
            </div>

            <div className="page_container">
              <Row className="driver_page_layout">
                <Col lg={8}>
                  <div className="trip_details_main_div driver_details_top_strip">
                    <div className="driver_details_identifier">
                      <h6>
                        <span className="driver_details_identifier_label">Employee ID:</span>
                        <span className="driver_details_identifier_value">
                          {driverRecord?.employeeNumber || "N/A"}
                        </span>
                      </h6>
                    </div>

                    <CustomTabs
                      tabs={DRIVER_TABS}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                    />
                  </div>

                  <div className="trip_information_main_div driver_form_shell driver_details_shell">
                    {showExpenseLogsTab ? (
                      <div className="driver_expense_logs_panel">
                        <NewTableComponent
                          columns={expenseColumns}
                          data={paginatedExpenseLogs}
                          currentPage={expenseCurrentPage}
                          setCurrentPage={setExpenseCurrentPage}
                          limit={expenseLimit}
                          setLimit={setExpenseLimit}
                          totalItems={expenseTotalItems}
                          totalPages={expenseTotalPages}
                          onSearch={setExpenseSearchText}
                          searchValue={expenseSearchText}
                          setSearchFilters={setExpenseSearchFilters}
                          searchFilters={expenseSearchFilters}
                          loading={getDriverExpensesPaginatedLoading}
                          paginationClassName="inline_pagination"
                          tableClassName="driver_expense_logs_table"
                          limitOptions={[8, 16, 24]}
                        />
                      </div>
                    ) : showHumanResourcesTab ? (
                      <div className="driver_form_content driver_hr_content">
                        {renderHumanResourcesSection({
                          title: "Attendance Log",
                          columns: attendanceColumns,
                          data: paginatedAttendanceLogs,
                          tableState: attendanceTable,
                          setTableState: setAttendanceTable,
                          totalItems: filteredAttendanceLogs.length,
                          totalPages: attendanceTotalPages,
                          tableClassName: "driver_hr_table driver_hr_attendance_table",
                          loading: getDriverAttendanceLoading,
                        })}

                        {renderHumanResourcesSection({
                          title: "Leave Logs",
                          columns: leaveColumns,
                          data: paginatedLeaveLogs,
                          tableState: leaveTable,
                          setTableState: setLeaveTable,
                          totalItems: filteredLeaveLogs.length,
                          totalPages: leaveTotalPages,
                          tableClassName: "driver_hr_table driver_hr_leave_table",
                          loading: getDriverLeavesLoading,
                        })}

                        {renderHumanResourcesSection({
                          title: "Payroll Logs",
                          columns: payrollColumns,
                          data: paginatedPayrollLogs,
                          tableState: payrollTable,
                          setTableState: setPayrollTable,
                          totalItems: filteredPayrollLogs.length,
                          totalPages: payrollTotalPages,
                          tableClassName: "driver_hr_table driver_hr_payroll_table",
                          loading: getDriverPayrollLoading,
                        })}
                      </div>
                    ) : showTrainingsTab ? (
                      renderTrainingsTabContent()
                    ) : (
                      <div className="driver_form_content">
                        <div className="driver_form_section">
                          <div className="driver_form_section_header">
                            <h3>General Information</h3>
                            <p>Main information for this driver record.</p>
                          </div>

                          <input
                            ref={profileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleProfilePictureChange}
                            style={{ display: "none" }}
                          />

                          <Row className="driver_detail_primary_row">
                            <Col
                              xl={1}
                              lg={2}
                              md={2}
                              sm={2}
                              xs={12}
                              className="driver_detail_avatar_col"
                            >
                              <button
                                type="button"
                                className="driver_detail_avatar_button"
                                onClick={() => profileInputRef.current?.click()}
                                title="Change profile photo"
                              >
                                {profilePreviewUrl ? (
                                  <img
                                    src={resolveDriverImageSrc(profilePreviewUrl)}
                                    alt="Driver profile preview"
                                    className="driver_detail_avatar_image"
                                  />
                                ) : (
                                  <FaUserCircle className="driver_detail_avatar_icon" />
                                )}
                                <span className="driver_detail_avatar_badge">
                                  <FaCamera />
                                </span>
                              </button>
                            </Col>

                            <Col
                              xl={5}
                              lg={5}
                              md={10}
                              sm={10}
                              xs={12}
                              className="driver_detail_name_col"
                            >
                              <Input
                                label={renderFieldLabel("Driver Name")}
                                type="text"
                                placeholder="Enter Name"
                                name="driverName"
                                value={values.driverName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.driverName}
                                touched={touched.driverName}
                              />
                            </Col>

                            <Col xl={6} lg={5} md={12}>
                              <Input
                                label={renderFieldLabel("Employee Number")}
                                type="text"
                                placeholder="Enter Employee Number"
                                name="employeeNumber"
                                value={values.employeeNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.employeeNumber}
                                touched={touched.employeeNumber}
                              />
                            </Col>

                            <Col xl={1} lg={2} md={2} className="d-none d-sm-block" />
                            <Col xl={5} lg={5} md={10} sm={10} xs={12}>
                              <Select
                                label={renderFieldLabel("Driver Supervisor")}
                                placeholder="Select Driver Supervisor"
                                options={userOptions}
                                value={
                                  userOptions.find(
                                    (opt) => String(opt.value) === String(values.supervisorId)
                                  ) || null
                                }
                                name="supervisorId"
                                onChange={(option) =>
                                  setFieldValue(
                                    "supervisorId",
                                    option ? option.value : ""
                                  )
                                }
                                onBlur={handleBlur}
                                error={errors.supervisorId}
                                touched={touched.supervisorId}
                                isClearable={true}
                              />
                            </Col>

                            <Col md={12}>{renderAssignedVehicleCard()}</Col>

                            <Col md={6}>
                              <CnicInput
                                label={renderFieldLabel("CNIC Number")}
                                name="cnicNumber"
                                value={values.cnicNumber}
                                onBlur={handleBlur}
                                error={errors.cnicNumber}
                                touched={touched.cnicNumber}
                                setFieldValue={setFieldValue}
                              />
                            </Col>

                            <Col md={6}>
                              <PhoneNumberInput
                                label={renderFieldLabel("Contact")}
                                placeholder="923XXXXXXXXX"
                                name="contact"
                                value={values.contact}
                                onBlur={handleBlur}
                                error={errors.contact}
                                touched={touched.contact}
                                setFieldValue={setFieldValue}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Date of Birth")}
                                type="date"
                                name="dateOfBirth"
                                value={values.dateOfBirth}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setValues({
                                    ...values,
                                    dateOfBirth: val,
                                  });
                                }}
                                onBlur={handleBlur}
                                error={errors.dateOfBirth}
                                touched={touched.dateOfBirth}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("CNIC Issue Date")}
                                type="date"
                                name="cnicIssueDate"
                                value={values.cnicIssueDate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  let calculatedExpiry = values.cnicExpiryDate;
                                  if (val) {
                                    const dateObj = new Date(val);
                                    if (!isNaN(dateObj.getTime())) {
                                      dateObj.setFullYear(dateObj.getFullYear() + 10);
                                      const y = dateObj.getFullYear();
                                      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
                                      const d = String(dateObj.getDate()).padStart(2, "0");
                                      calculatedExpiry = `${y}-${m}-${d}`;
                                    }
                                  }
                                  setValues({
                                    ...values,
                                    cnicIssueDate: val,
                                    cnicExpiryDate: calculatedExpiry,
                                  });
                                }}
                                onBlur={handleBlur}
                                error={errors.cnicIssueDate}
                                touched={touched.cnicIssueDate}
                              />
                            </Col>

                          <Col md={6}>
                            <Input
                              label={renderFieldLabel("CNIC Expiry Date")}
                              type="date"
                              name="cnicExpiryDate"
                              value={values.cnicExpiryDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.cnicExpiryDate}
                              touched={touched.cnicExpiryDate}
                            />
                          </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("License Number")}
                                type="text"
                                placeholder="Enter License Number"
                                name="licenseNumber"
                                value={values.licenseNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.licenseNumber}
                                touched={touched.licenseNumber}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("License Issue Date")}
                                type="date"
                                name="licenseIssueDate"
                                value={values.licenseIssueDate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  let calculatedExpiry = values.licenseExpiryDate;
                                  if (val) {
                                    const dateObj = new Date(val);
                                    if (!isNaN(dateObj.getTime())) {
                                      dateObj.setFullYear(dateObj.getFullYear() + 5);
                                      const y = dateObj.getFullYear();
                                      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
                                      const d = String(dateObj.getDate()).padStart(2, "0");
                                      calculatedExpiry = `${y}-${m}-${d}`;
                                    }
                                  }
                                  setValues({
                                    ...values,
                                    licenseIssueDate: val,
                                    licenseExpiryDate: calculatedExpiry,
                                  });
                                }}
                                onBlur={handleBlur}
                                error={errors.licenseIssueDate}
                                touched={touched.licenseIssueDate}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("License Expiry Date")}
                                type="date"
                                name="licenseExpiryDate"
                                value={values.licenseExpiryDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.licenseExpiryDate}
                                touched={touched.licenseExpiryDate}
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="driver_form_section">
                          <div className="driver_form_section_header">
                            <h3>App Authentication</h3>
                            <p>Access and sign-in settings for the driver app.</p>
                          </div>

                          <Row>
                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("App Auth Code")}
                                type="text"
                                name="appAuthCode"
                                value={values.appAuthCode}
                                onBlur={handleBlur}
                                error={errors.appAuthCode}
                                touched={touched.appAuthCode}
                                disabled
                                showIcon={refreshIcon}
                                onIconClick={
                                  generateDriverPasswordLoading
                                    ? null
                                    : () => handleGenerateDriverPassword(setFieldValue)
                                }
                              />
                            </Col>

                            <Col md={6}>
                              <Select
                                label={renderFieldLabel("Auth Method")}
                                placeholder="Select auth method"
                                options={AUTH_METHOD_OPTIONS}
                                loading={false}
                                value={
                                  AUTH_METHOD_OPTIONS.find(
                                    (option) => option.value === values.authMethod
                                  ) || null
                                }
                                name="authMethod"
                                onChange={(option) =>
                                  setFieldValue("authMethod", option ? option.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.authMethod}
                                touched={touched.authMethod}
                                isSearch={false}
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="driver_form_section">
                          <div className="driver_form_section_header">
                            <h3>Human Resources</h3>
                            <p>Compensation and leave quota details for this driver.</p>
                          </div>

                          <Row>
                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Base Salary")}
                                type="number"
                                placeholder="00"
                                name="baseSalary"
                                value={values.baseSalary}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.baseSalary}
                                touched={touched.baseSalary}
                                showExtraText
                                extraText="PKR"
                              />
                            </Col>

                            <Col md={6}>
                              <Select
                                label={renderFieldLabel("Salary Terms")}
                                placeholder="Select salary terms"
                                options={SALARY_TERMS_OPTIONS}
                                loading={false}
                                value={
                                  SALARY_TERMS_OPTIONS.find(
                                    (option) => option.value === values.salaryTerms
                                  ) || null
                                }
                                name="salaryTerms"
                                onChange={(option) =>
                                  setFieldValue("salaryTerms", option ? option.value : "")
                                }
                                onBlur={handleBlur}
                                error={errors.salaryTerms}
                                touched={touched.salaryTerms}
                                isSearch={false}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Casual Leave Quota")}
                                type="number"
                                placeholder="0"
                                name="casualLeaveQuota"
                                value={values.casualLeaveQuota}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.casualLeaveQuota}
                                touched={touched.casualLeaveQuota}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Annual Leave Quota")}
                                type="number"
                                placeholder="0"
                                name="annualLeaveQuota"
                                value={values.annualLeaveQuota}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.annualLeaveQuota}
                                touched={touched.annualLeaveQuota}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Sick Leave Quota")}
                                type="number"
                                placeholder="0"
                                name="sickLeaveQuota"
                                value={values.sickLeaveQuota}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.sickLeaveQuota}
                                touched={touched.sickLeaveQuota}
                              />
                            </Col>

                            <Col md={6}>
                              <Input
                                label={renderFieldLabel("Paternity Leave Quota")}
                                type="number"
                                placeholder="0"
                                name="paternityLeaveQuota"
                                value={values.paternityLeaveQuota}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.paternityLeaveQuota}
                                touched={touched.paternityLeaveQuota}
                              />
                            </Col>
                          </Row>
                        </div>

                        {/* ── Section 1: Allowances ─────────────────────────────── */}
                        <div className="driver_form_section">
                          <div className="driver_allowances_section_header">
                            <div>
                              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>Allowances</h3>
                              <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>Food, TA/DA, Trip, Mobile allowances for this driver.</p>
                            </div>
                            <button
                              type="button"
                              className="driver_allowance_add_btn"
                              disabled={!!allowanceForm.editingId || allowanceSaving}
                              onClick={() => { setArrearsForm(EMPTY_ARREARS_FORM); setAllowanceForm({ ...EMPTY_ALLOWANCE_FORM, editingId: "new" }); }}
                            >
                              <FiPlus style={{ fontSize: "14px" }} /> Add
                            </button>
                          </div>

                          {allowanceForm.editingId && (
                            <div className="driver_allowances_form">
                              <Row>
                                <Col md={4}>
                                  <Select
                                    label={renderFieldLabel("Allowance Type", true)}
                                    placeholder="Select type"
                                    options={ALLOWANCE_TYPE_OPTIONS}
                                    value={ALLOWANCE_TYPE_OPTIONS.find((o) => o.value === allowanceForm.allowanceType) || null}
                                    onChange={(opt) => { setAllowanceForm((f) => ({ ...f, allowanceType: opt ? opt.value : "" })); setAllowanceErrors((e) => ({ ...e, allowanceType: "" })); }}
                                    isSearch={false}
                                  />
                                  {allowanceErrors.allowanceType && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{allowanceErrors.allowanceType}</p>}
                                </Col>
                                <Col md={4}>
                                  <Input
                                    label={renderFieldLabel("Amount (PKR)", true)}
                                    type="number"
                                    placeholder="0"
                                    value={allowanceForm.amount}
                                    onChange={(e) => { setAllowanceForm((f) => ({ ...f, amount: e.target.value })); setAllowanceErrors((e) => ({ ...e, amount: "" })); }}
                                  />
                                  {allowanceErrors.amount && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{allowanceErrors.amount}</p>}
                                </Col>
                                <Col md={4}>
                                  <Select
                                    label={renderFieldLabel("Frequency", true)}
                                    placeholder="Select frequency"
                                    options={ALLOWANCE_FREQUENCY_OPTIONS}
                                    value={ALLOWANCE_FREQUENCY_OPTIONS.find((o) => o.value === allowanceForm.frequency) || null}
                                    onChange={(opt) => { setAllowanceForm((f) => ({ ...f, frequency: opt ? opt.value : "" })); setAllowanceErrors((e) => ({ ...e, frequency: "" })); }}
                                    isSearch={false}
                                  />
                                  {allowanceErrors.frequency && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{allowanceErrors.frequency}</p>}
                                </Col>
                                <Col md={12} className="d-flex justify-content-end gap-2">
                                  <button type="button" onClick={handleAddAllowance} disabled={allowanceSaving} className="driver_allowance_save_btn">
                                    {allowanceSaving ? "Saving..." : allowanceForm.editingId === "new" ? "Save" : "Update"}
                                  </button>
                                  <button type="button" onClick={() => { setAllowanceForm(EMPTY_ALLOWANCE_FORM); setAllowanceErrors({}); }} className="driver_allowance_cancel_btn">Cancel</button>
                                </Col>
                              </Row>
                            </div>
                          )}

                          <div className="driver_allowances_table_wrapper">
                            <table className="driver_allowances_table">
                              <thead>
                                <tr><th>Type</th><th>Amount (PKR)</th><th>Frequency</th><th>Actions</th></tr>
                              </thead>
                              <tbody>
                                {allowances.filter((a) => ALLOWANCE_SECTION_TYPES.includes(a.allowance_type)).map((item) => (
                                  <tr key={item.id} className={allowanceForm.editingId === item.id ? "driver_allowance_editing_row" : ""}>
                                    <td>{ALLOWANCE_TYPE_LABELS[item.allowance_type] || item.allowance_type}</td>
                                    <td>{Number(item.amount).toLocaleString()}</td>
                                    <td style={{ textTransform: "capitalize" }}>{item.frequency?.replace("_", " ")}</td>
                                    <td>
                                      <div className="driver_allowance_actions">
                                        <button type="button" onClick={() => handleEditAllowance(item)} className="driver_allowance_edit_btn"><LuPencil /></button>
                                        <button type="button" onClick={() => handleDeleteAllowance(item)} disabled={allowanceSaving} className="driver_allowance_delete_btn"><LuTrash2 /></button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                                {allowances.filter((a) => ALLOWANCE_SECTION_TYPES.includes(a.allowance_type)).length === 0 && !allowanceForm.editingId && (
                                  <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: "18px 12px", fontSize: "13px" }}>No allowances added yet.</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* ── Section 2: Arrears ────────────────────────────────── */}
                        <div className="driver_form_section">
                          <div className="driver_allowances_section_header">
                            <div>
                              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#1e293b" }}>Arrears</h3>
                              <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>Give arrears to driver or take from driver.</p>
                            </div>
                            <button
                              type="button"
                              className="driver_allowance_add_btn"
                              disabled={!!arrearsForm.editingId || allowanceSaving}
                              onClick={() => { setAllowanceForm(EMPTY_ALLOWANCE_FORM); setArrearsForm({ ...EMPTY_ARREARS_FORM, editingId: "new" }); }}
                            >
                              <FiPlus style={{ fontSize: "14px" }} /> Add
                            </button>
                          </div>

                          {arrearsForm.editingId && (
                            <div className="driver_allowances_form">
                              <Row>
                                <Col md={4}>
                                  <Select
                                    label={renderFieldLabel("Direction", true)}
                                    placeholder="Select direction"
                                    options={ARREARS_DIRECTION_OPTIONS}
                                    value={ARREARS_DIRECTION_OPTIONS.find((o) => o.value === arrearsForm.direction) || null}
                                    onChange={(opt) => { setArrearsForm((f) => ({ ...f, direction: opt ? opt.value : "" })); setArrearsErrors((e) => ({ ...e, direction: "" })); }}
                                    isSearch={false}
                                  />
                                  {arrearsErrors.direction && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{arrearsErrors.direction}</p>}
                                </Col>
                                <Col md={4}>
                                  <Input
                                    label={renderFieldLabel("Amount (PKR)", true)}
                                    type="number"
                                    placeholder="0"
                                    value={arrearsForm.amount}
                                    onChange={(e) => { setArrearsForm((f) => ({ ...f, amount: e.target.value })); setArrearsErrors((e) => ({ ...e, amount: "" })); }}
                                  />
                                  {arrearsErrors.amount && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{arrearsErrors.amount}</p>}
                                </Col>
                                <Col md={4}>
                                  <Select
                                    label={renderFieldLabel("Frequency", true)}
                                    placeholder="Select frequency"
                                    options={BONUS_DEDUCTION_FREQUENCY_OPTIONS}
                                    value={BONUS_DEDUCTION_FREQUENCY_OPTIONS.find((o) => o.value === arrearsForm.frequency) || null}
                                    onChange={(opt) => { setArrearsForm((f) => ({ ...f, frequency: opt ? opt.value : "" })); setArrearsErrors((e) => ({ ...e, frequency: "" })); }}
                                    isSearch={false}
                                  />
                                  {arrearsErrors.frequency && <p style={{ color: "#dc2626", fontSize: "12px", marginTop: "-8px", marginBottom: "8px" }}>{arrearsErrors.frequency}</p>}
                                </Col>
                                <Col md={12} className="d-flex justify-content-end gap-2">
                                  <button type="button" onClick={handleSaveArrears} disabled={allowanceSaving} className="driver_allowance_save_btn">
                                    {allowanceSaving ? "Saving..." : arrearsForm.editingId === "new" ? "Save" : "Update"}
                                  </button>
                                  <button type="button" onClick={() => { setArrearsForm(EMPTY_ARREARS_FORM); setArrearsErrors({}); }} className="driver_allowance_cancel_btn">Cancel</button>
                                </Col>
                              </Row>
                            </div>
                          )}

                          <div className="driver_allowances_table_wrapper">
                            <table className="driver_allowances_table">
                              <thead>
                                <tr><th>Direction</th><th>Amount (PKR)</th><th>Frequency</th><th>Actions</th></tr>
                              </thead>
                              <tbody>
                                {allowances.filter((a) => ARREARS_SECTION_TYPES.includes(a.allowance_type)).map((item) => (
                                  <tr key={item.id} className={arrearsForm.editingId === item.id ? "driver_allowance_editing_row" : ""}>
                                    <td>
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                        {item.allowance_type === "arrears_give"
                                          ? <span style={{ color: "#16a34a", fontWeight: 600 }}>▲ Give to Driver</span>
                                          : <span style={{ color: "#dc2626", fontWeight: 600 }}>▼ Take from Driver</span>}
                                      </span>
                                    </td>
                                    <td>{Number(item.amount).toLocaleString()}</td>
                                    <td style={{ textTransform: "capitalize" }}>{item.frequency?.replace("_", " ")}</td>
                                    <td>
                                      <div className="driver_allowance_actions">
                                        <button type="button" onClick={() => handleEditArrears(item)} className="driver_allowance_edit_btn"><LuPencil /></button>
                                        <button type="button" onClick={() => handleDeleteAllowance(item)} disabled={allowanceSaving} className="driver_allowance_delete_btn"><LuTrash2 /></button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                                {allowances.filter((a) => ARREARS_SECTION_TYPES.includes(a.allowance_type)).length === 0 && !arrearsForm.editingId && (
                                  <tr><td colSpan={4} style={{ textAlign: "center", color: "#94a3b8", padding: "18px 12px", fontSize: "13px" }}>No arrears configured.</td></tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>


                        <div className="driver_form_section">
                          <div className="driver_form_section_header">
                            <h3>Attachments</h3>
                            <p>Upload supporting files and required documents.</p>
                          </div>

                          <div className="issue_file_upload_wrapper">
                            <label>Attachment</label>
                            <FileUpload
                              destination="driver-safety"
                              files={values.attachments}
                              setFiles={updateAttachments}
                              maxFileSize={10}
                              maxFiles={5}
                              className="issue_file_upload create_vehicle_upload"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>

                <Col lg={4}>
                  <div className="service_cost_summary_details assets_vehicle_right_sidebar driver_details_sidebar">
                    <div className="service_cost_summary mb-3">
                      <h6>Cost Summary</h6>
                      <p>Financial Breakdown</p>

                      <div className="service_item">
                        <div>Trip Expenses (PKR)</div>
                        <div>{formatDriverAmount(costSummary?.tripExpenses)}</div>
                      </div>
                      <div className="service_item">
                        <div>Refuelling (PKR)</div>
                        <div>{formatDriverAmount(costSummary?.refuelling)}</div>
                      </div>
                      <div className="service_item">
                        <div>Deductions (PKR)</div>
                        <div>{formatDriverAmount(costSummary?.deductions)}</div>
                      </div>
                      <div className="total">
                        <div>Total Net Salary (PKR)</div>
                        <span>{formatDriverAmount(totalNetSalary)}</span>
                      </div>
                    </div>

                    <LogsComments
                      key={driverHistoryId || id || "driver-history"}
                      historyItems={resolvedHistoryItems}
                      onFetchHistory={handleFetchDriverHistory}
                      onSubmitComment={handleSubmitDriverComment}
                      loading={driverHistoryLoading || commentOnDriverLoading}
                      editorModules={modules}
                      editorFormats={formats}
                      emptyMessages={{
                        all: "No comments or activity found",
                        comment: "No comments found",
                        logs: "No activity found",
                      }}
                    />

                    <div className="service_cost_summary_details rfq_right_card driver_details_notes_card">
                      <div className="parts_general_notes">
                        <h5>General Notes</h5>
                        <p>Add any additional notes or observations here.</p>
                        <ReactQuill
                          placeholder="write notes here..."
                          modules={modules}
                          theme="snow"
                          value={values.notes || ""}
                          onChange={(nextValue) => setFieldValue("notes", nextValue)}
                          formats={formats}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                        {touched.notes && errors.notes ? (
                          <div className="driver_details_notes_error">{errors.notes}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <AttachmentViewerModal
              show={attachmentPreviewFiles.length > 0}
              files={attachmentPreviewFiles}
              heading="Expense Attachments"
              handleClose={() => setAttachmentPreviewFiles([])}
            />
          </div>
        );
      }}
    </Formik>
  );
};

export default DriverDetails;
